/**
 * Poker hand evaluation (high hands). Produces a single comparable integer per 5-card hand:
 *   category * 15^5 + tie-breakers (base 15, most significant first).
 * Best-of-N helpers support Hold'em (any 5 of 7) and Omaha (exactly 2 hole + 3 board).
 */
import { parseCard, type CardId } from '../../core/cards.js';

export type PokerCategory =
  | 'HIGH_CARD'
  | 'PAIR'
  | 'TWO_PAIR'
  | 'THREE_OF_A_KIND'
  | 'STRAIGHT'
  | 'FLUSH'
  | 'FULL_HOUSE'
  | 'FOUR_OF_A_KIND'
  | 'STRAIGHT_FLUSH';

export const POKER_CATEGORIES: readonly PokerCategory[] = [
  'HIGH_CARD',
  'PAIR',
  'TWO_PAIR',
  'THREE_OF_A_KIND',
  'STRAIGHT',
  'FLUSH',
  'FULL_HOUSE',
  'FOUR_OF_A_KIND',
  'STRAIGHT_FLUSH',
];

const B = 15;
const B5 = B ** 5;

/** Compact numeric card for hot loops: rank (2..14) * 4 + suitIndex. */
export type FastCard = number;
const SUIT_IDX = { S: 0, H: 1, D: 2, C: 3 } as const;
export const toFast = (id: CardId): FastCard => {
  const c = parseCard(id);
  return c.rank * 4 + SUIT_IDX[c.suit];
};

function value(cat: number, ks: number[]): number {
  let v = cat * B5;
  let mul = B5 / B;
  for (const k of ks) {
    v += k * mul;
    mul /= B;
  }
  return v;
}

const counts = new Int8Array(15);

/** Evaluates exactly five fast cards. */
export function eval5(c0: number, c1: number, c2: number, c3: number, c4: number): number {
  const r = [c0 >> 2, c1 >> 2, c2 >> 2, c3 >> 2, c4 >> 2].sort((a, b) => b - a) as number[];
  const s0 = c0 & 3;
  const flush = (c1 & 3) === s0 && (c2 & 3) === s0 && (c3 & 3) === s0 && (c4 & 3) === s0;
  let straightHigh = 0;
  if (r[0]! - r[4]! === 4 && new Set(r).size === 5) straightHigh = r[0]!;
  else if (r[0] === 14 && r[1] === 5 && r[2] === 4 && r[3] === 3 && r[4] === 2) straightHigh = 5;
  if (straightHigh && flush) return value(8, [straightHigh]);
  counts.fill(0);
  for (const x of r) counts[x]!++;
  // Group ranks by (count desc, rank desc).
  const groups: [number, number][] = [];
  for (let rank = 14; rank >= 2; rank--) if (counts[rank]) groups.push([counts[rank]!, rank]);
  groups.sort((a, b) => b[0] - a[0] || b[1] - a[1]);
  const g0 = groups[0]!;
  const g1 = groups[1];
  if (g0[0] === 4) return value(7, [g0[1], g1![1]]);
  if (g0[0] === 3 && g1 && g1[0] === 2) return value(6, [g0[1], g1[1]]);
  if (flush) return value(5, r);
  if (straightHigh) return value(4, [straightHigh]);
  if (g0[0] === 3) return value(3, groups.map((g) => g[1]));
  if (g0[0] === 2 && g1 && g1[0] === 2) return value(2, groups.map((g) => g[1]));
  if (g0[0] === 2) return value(1, groups.map((g) => g[1]));
  return value(0, r);
}

export function categoryOf(v: number): PokerCategory {
  return POKER_CATEGORIES[Math.floor(v / B5)]!;
}

/** Best 5-card value from any 5 of the given cards (5..7 typical). */
export function bestOfAny(cards: readonly FastCard[]): number {
  const n = cards.length;
  if (n < 5) throw new Error('Need at least 5 cards');
  let best = -1;
  for (let a = 0; a < n - 4; a++)
    for (let b = a + 1; b < n - 3; b++)
      for (let c = b + 1; c < n - 2; c++)
        for (let d = c + 1; d < n - 1; d++)
          for (let e = d + 1; e < n; e++) {
            const v = eval5(cards[a]!, cards[b]!, cards[c]!, cards[d]!, cards[e]!);
            if (v > best) best = v;
          }
  return best;
}

/** Omaha: exactly `useHole` hole cards and 5-useHole board cards. */
export function bestOfExact(hole: readonly FastCard[], board: readonly FastCard[], useHole: number): number {
  const holeCombos = combos(hole, useHole);
  const boardCombos = combos(board, 5 - useHole);
  let best = -1;
  for (const h of holeCombos)
    for (const b of boardCombos) {
      const all = [...h, ...b];
      const v = eval5(all[0]!, all[1]!, all[2]!, all[3]!, all[4]!);
      if (v > best) best = v;
    }
  return best;
}

function combos<T>(arr: readonly T[], k: number): T[][] {
  const out: T[][] = [];
  const cur: T[] = [];
  const rec = (start: number) => {
    if (cur.length === k) {
      out.push([...cur]);
      return;
    }
    for (let i = start; i <= arr.length - (k - cur.length); i++) {
      cur.push(arr[i]!);
      rec(i + 1);
      cur.pop();
    }
  };
  rec(0);
  return out;
}

export function evaluateHoldem(hole: readonly CardId[], board: readonly CardId[]): number {
  return bestOfAny([...hole, ...board].map(toFast));
}
